import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  PFC_APPLICATION_CREATE,
  PFC_APPLICATION_EDIT,
  PFC_APPLICATION_FORM_ALL,
  PFC_APPLICATION_FORM_CURRENT,
  PFC_APPLICATION_FORM_FIELD_BATCH,
  PFC_APPLICATION_FORM_FIELD_BATCH_CREATE,
  PFC_APPLICATION_LIST,
  PFC_APPLICATION_RESET_PASSWORD,
  PFC_APPLICATION_STATUS,
  PFC_APPLICATION_UPDATE,
  PFC_PAYMENT_ALL,
  PFC_PAYMENT_CONFIRM,
  PFC_PAYMENT_CREATE,
  PFC_PAYMENT_LIST,
  PFC_PAYMENT_PRINCIPAL,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const pfcApplicationCreate = createAsyncThunk(
  PFC_APPLICATION_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationList = createAsyncThunk(
  PFC_APPLICATION_LIST,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationEdit = createAsyncThunk(
  PFC_APPLICATION_EDIT,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_EDIT}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationEdit(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationStatus = createAsyncThunk(
  PFC_APPLICATION_STATUS,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_STATUS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationStatus(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationResetPassword = createAsyncThunk(
  PFC_APPLICATION_RESET_PASSWORD,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_RESET_PASSWORD}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationResetPassword(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationUpdate = createAsyncThunk(
  PFC_APPLICATION_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_UPDATE}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcApplicationUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationFormAll = createAsyncThunk(
  PFC_APPLICATION_FORM_ALL,
  async (
    params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_FORM_ALL}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.pfcApplicationFormAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationFieldBatchCreate = createAsyncThunk(
  PFC_APPLICATION_FORM_FIELD_BATCH_CREATE,
  async (
    params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_FORM_FIELD_BATCH}`, 'post'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.pfcApplicationFieldBatchCreate(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationFormCurrent = createAsyncThunk(
  PFC_APPLICATION_FORM_CURRENT,
  async (
    params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_FORM_CURRENT}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.pfcApplicationFormCurrent(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationFormFieldById = createAsyncThunk(
  PFC_APPLICATION_FORM_FIELD_BATCH,
  async (
    params: PayloadCreatorParams<`${typeof API.PFC_APPLICATION_FORM_FIELD_BATCH}{id}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.pfcApplicationFormFieldById(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcPaymentAll = createAsyncThunk(
  PFC_PAYMENT_ALL,
  async (params: PayloadCreatorParams<`${typeof API.PFC_PAYMENT_ALL}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.pfcPaymentAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcPaymentCreate = createAsyncThunk(
  PFC_PAYMENT_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.PFC_PAYMENT_CREATE}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcPaymentCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcPaymentList = createAsyncThunk(
  PFC_PAYMENT_LIST,
  async (params: PayloadCreatorParams<`${typeof API.PFC_PAYMENT_LIST}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcPaymentList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcPaymentPrincipal = createAsyncThunk(
  PFC_PAYMENT_PRINCIPAL,
  async (
    params: PayloadCreatorParams<`${typeof API.PFC_PAYMENT_PRINCIPAL}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.pfcPaymentPrincipal(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcPaymentConfirm = createAsyncThunk(
  PFC_PAYMENT_CONFIRM,
  async (params: PayloadCreatorParams<`${typeof API.PFC_PAYMENT_CONFIRM}{id}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.pfcPaymentConfirm(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const pfcApplicationEditClear = createAction(`${PFC_APPLICATION_EDIT}/clear`)

export const pfcApplicationListClear = createAction(`${PFC_APPLICATION_LIST}/clear`)

export const pfcApplicationFormAllClear = createAction(`${PFC_APPLICATION_FORM_ALL}/clear`)

export const pfcApplicationFormFieldByIdClear = createAction(`${PFC_APPLICATION_FORM_FIELD_BATCH}/clear`)

export const pfcApplicationFormCurrentClear = createAction(`${PFC_APPLICATION_FORM_CURRENT}/clear`)

export const pfcPaymentAllClear = createAction(`${PFC_PAYMENT_ALL}/clear`)

export const pfcPaymentListClear = createAction(`${PFC_PAYMENT_LIST}/clear`)

export const pfcPaymentPrincipalClear = createAction(`${PFC_PAYMENT_PRINCIPAL}/clear`)
